/**
 * GenericQuestionContent.tsx
 * Generic question content renderer for the new HTML-based exam structure
 * Supports any type of question or parts with flexible HTML content
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@vsat/shared/lib/utils';
import { Card } from '@vsat/shared/components';
import { Label } from '@vsat/shared/components';
import { RadioGroup, RadioGroupItem } from '@vsat/shared/components';
import { MatchingQuestion } from '../MatchingQuestion';
import QuestionImage from '../QuestionImage';
import HtmlContent from '../HtmlContent';
import FillBlankQuestion from '../FillBlankQuestion';
import EnhancedFillBlankQuestion from '../EnhancedFillBlankQuestion';
import type { DemoAnswer, DemoQuestion } from '../types';
import EssayQuestion from './EssayQuestion';
import QuestionHeader from './QuestionHeader';
import TrueFalseQuestion from './TrueFalseQuestion';

interface Props {
  question: DemoQuestion;
  answer: DemoAnswer | undefined;
  questionIndex: number;
  totalQuestions: number;
  isExpired: boolean;
  onChange: (
    value: string | number | boolean | Record<string, string | null> | Record<string, boolean>
  ) => void;
  onToggleFlag: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onSubmit: () => void;
  allAnswers?: Record<string, DemoAnswer>; // All answers from all questions
  allQuestions?: DemoQuestion[]; // All questions in the exam (for finding cluster questions)
  shuffledOrders?: Record<string, string[]>; // Stable shuffled order for matching questions keyed by question ID
  onShuffledOrderChange?: (questionId: string, order: string[]) => void; // Callback to save shuffled order
  onNavigateToQuestion?: (questionIndex: number) => void; // Callback to navigate to a specific question
  examStandard?: string | null; // Exam standard: 'VSAT', 'THPT', or null
  examSubject?: string; // Subject name from exam info (overrides question.subject)
}

export default function GenericQuestionContent({
  question,
  answer,
  questionIndex,
  totalQuestions,
  isExpired,
  onChange,
  onToggleFlag,
  onPrevious,
  onSubmit,
  allAnswers,
  allQuestions,
  shuffledOrders,
  onShuffledOrderChange,
  onNavigateToQuestion,
  examStandard,
  examSubject,
}: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const normalizeBlankId = (rawId?: string | null | any): string | null => {
    if (!rawId) return null;
    // Handle case where rawId might be an object (e.g., from fillBlankAnswers)
    const idString =
      typeof rawId === 'string' ? rawId : rawId?.id || rawId?.blankId || String(rawId);
    if (!idString || typeof idString !== 'string') return null;
    const cleaned = idString.replace(/[{}]/g, '').trim();
    if (!cleaned) return null;

    const explicitMatch = cleaned.match(/blank\s*[-_]?(\d+)/i);
    if (explicitMatch) {
      return `blank${explicitMatch[1]}`;
    }

    const containsBlank = cleaned.toLowerCase().includes('blank');
    const suffixDigits = cleaned.match(/(\d+)$/);
    if (containsBlank && suffixDigits) {
      return `blank${suffixDigits[1]}`;
    }

    return cleaned;
  };

  // Helper function to determine layout based on question type
  const getQuestionLayout = useCallback(() => {
    // For trueFalse, mc, fillBlank, and all short question types, use side-by-side layout
    // short types: 'short' (from short_numeric), 'short_text', and 'fillBlank' (from cloze)
    if (
      question.type === 'trueFalse' ||
      question.type === 'mc' ||
      question.type === 'fillBlank' ||
      question.type === 'short_text' ||
      question.type === 'short' // short_numeric mapped to 'short'
    ) {
      return 'side-by-side';
    }

    // For matching questions, use row-by-row layout
    if (question.type === 'matching') {
      return 'row-by-row';
    }

    // Essay and other long-form questions use row-by-row layout
    if (question.type === 'essay') {
      return 'row-by-row';
    }

    // Default to row-by-row for other question types
    return 'row-by-row';
  }, [question.type]);

  // Auto-focus the correct input when navigating to a cloze question
  const getQuestionTypeLabel = () => {
    switch (question.type) {
      case 'mc':
        return t('landing.questions.types.multipleChoice');
      case 'tf':
        return t('landing.questions.types.trueFalse');
      case 'trueFalse':
        return t('landing.questions.types.trueFalseExtended');
      case 'matching':
        return t('landing.questions.types.matching');
      case 'short':
        return t('landing.questions.types.shortAnswer');
      case 'essay':
        return t('landing.questions.types.essay');
      case 'fillBlank':
      case 'short_text':
        return t('landing.questions.types.fillBlank');
      default:
        return t('landing.questions.types.default');
    }
  };

  // Helper function to determine if we should show the part header
  const shouldShowPartHeader = () => {
    return Boolean(question.clusterContext);
  };

  // Helper function to get part title from database cluster context
  const getPartTitle = () => {
    // Always prioritize cluster context from database
    if (question.clusterContext?.title) {
      return question.clusterContext.title;
    }

    // Generic fallback for any exam structure (should rarely be used if DB is properly seeded)
    const questionNumber = questionIndex + 1;
    return t('landing.questions.questionNumber', { number: questionNumber });
  };

  // Helper function to get part description
  // Supports per-type instructions from instructionsByType (for mixed-type clusters)
  const getPartDescription = () => {
    const ctx = question.clusterContext;
    if (!ctx) return '';

    // Check for per-type instructions first (new feature for mixed-type clusters)
    if (ctx.instructionsByType && typeof ctx.instructionsByType === 'object') {
      // Map frontend question type to backend requirement type key
      const typeMap: Record<string, string> = {
        mc: 'mcq',
        tf: 'tf_matrix',
        trueFalse: 'tf_matrix',
        matching: 'matching',
        short: 'short_numeric',
        short_text: 'short_text',
        fillBlank: 'cloze',
        essay: 'essay',
      };
      const backendType = typeMap[question.type] || question.type;
      if (ctx.instructionsByType[backendType]) {
        return ctx.instructionsByType[backendType];
      }
    }

    // Fallback to unified content
    if (ctx.content) {
      return ctx.content;
    }
    return '';
  };

  // Helper function to get stimulusData for current question
  // Handles both legacy (stimulusData as map) and new structure (stimulusDataMap + hasMultipleSubClusters)
  const getStimulusData = useCallback(() => {
    if (!question.clusterContext) {
      return null;
    }

    const clusterContext = question.clusterContext as any;
    const questionClusterId = question.questionClusterId;

    // NEW STRUCTURE: Check for hasMultipleSubClusters + stimulusDataMap
    if (clusterContext.hasMultipleSubClusters && clusterContext.stimulusDataMap) {
      // CRITICAL FIX: Only return stimulus if ID matches. Do NOT fall back.
      if (questionClusterId && clusterContext.stimulusDataMap[questionClusterId]) {
        return clusterContext.stimulusDataMap[questionClusterId];
      }
      return null;
    }

    // CURRENT STRUCTURE: Check for stimulusData
    if (!clusterContext.stimulusData) {
      return null;
    }

    const stimulusData = clusterContext.stimulusData;

    // Handle object structure (can be a map or a single stimulus object)
    if (typeof stimulusData === 'object' && !Array.isArray(stimulusData) && stimulusData !== null) {
      // 1. Check if this is a map of questionClusterId -> stimulusData
      if (questionClusterId && stimulusData[questionClusterId]) {
        return stimulusData[questionClusterId];
      }

      // 2. Check if it's a single stimulusData object (has content/stimulusText/title directly)
      if (stimulusData.content || stimulusData.stimulusText || stimulusData.title) {
        const stimulusOwnerClusterId = stimulusData.questionClusterId;
        if (!stimulusOwnerClusterId) {
          // No questionClusterId on stimulusData → shared/global stimulus, always show
          return stimulusData;
        }

        // Stimulus is specific to a sub-cluster. Match exactly.
        if (questionClusterId === stimulusOwnerClusterId) {
          return stimulusData;
        }

        // No match found for sub-cluster specific stimulus
        return null;
      }

      // If it's an object but doesn't match above, it's likely a map without our key
      return null;
    }

    // If stimulusData is a string, return it directly
    return stimulusData;
  }, [question.clusterContext, question.questionClusterId]);

  const renderClusterMeta = () => {
    if (
      typeof question.clusterQuestionIndex !== 'number' ||
      !question.clusterQuestionCount ||
      question.clusterQuestionCount <= 1
    ) {
      return null;
    }

    return (
      <p className='text-xs text-muted-foreground mt-1'>
        {t('landing.exam.layout.clusterProgress', {
          current: question.clusterQuestionIndex + 1,
          total: question.clusterQuestionCount,
          defaultValue: `Câu ${question.clusterQuestionIndex + 1}/${question.clusterQuestionCount} trong phần này`,
        })}
      </p>
    );
  };

  // Helper function to extract blank ID from question context
  // Examples: "Fill in blank21" -> "blank21", "Fill blank22" -> "blank22"
  const extractBlankIdFromContext = (context: string | undefined): string | null => {
    if (!context) return null;

    const textContent = context
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const match = textContent.match(/([A-Za-z0-9-_]*blank[-_\s]*\d+)/i);
    if (match) {
      return normalizeBlankId(match[1]);
    }

    return null;
  };

  const getQuestionBlankId = (targetQuestion?: DemoQuestion): string | null => {
    if (!targetQuestion) return null;

    if (targetQuestion.fillBlankAnswers && targetQuestion.fillBlankAnswers.length > 0) {
      // Handle both array of strings and array of objects with blankId
      const fromAnswers = targetQuestion.fillBlankAnswers
        .map(answer => {
          if (typeof answer === 'string') {
            return normalizeBlankId(answer);
          } else if (answer && typeof answer === 'object') {
            // Handle object with blankId field
            return normalizeBlankId((answer as any).blankId || (answer as any).id);
          }
          return null;
        })
        .find(Boolean);
      if (fromAnswers) {
        return fromAnswers;
      }
    }

    const fromId = normalizeBlankId(targetQuestion.id);
    if (fromId) {
      return fromId;
    }

    return extractBlankIdFromContext(targetQuestion.stem);
  };

  const normalizeClozeTemplate = (content?: string | null) => {
    if (!content) return '';

    // CRITICAL: Decode HTML entities first to ensure [blank] markers are found
    let decoded = content
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    // First, normalize {{blankXX}} format
    let normalized = decoded.replace(/\{\{([^}]+)\}\}/g, (_, inner) => {
      const normalizedId = normalizeBlankId(inner);
      return normalizedId ? `{{${normalizedId}}}` : `{{${inner}}}`;
    });

    // Convert [blank] format to {{blankXX}} format
    // Find all [blank] occurrences and replace with {{blankXX}} based on order
    // CRITICAL: For multiple sub-clusters, only use questions with the same questionClusterId
    if (normalized.includes('[blank]') && allQuestions) {
      // CRITICAL: For multiple sub-clusters, only get questions with the same questionClusterId
      // For single sub-cluster, get all questions in cluster
      const clusterQuestions = allQuestions
        .filter(q => {
          // For multiple sub-clusters, only get questions with the same questionClusterId
          if (question.questionClusterId) {
            return (
              q.clusterId === question.clusterId &&
              q.type === 'fillBlank' &&
              (q as any).questionClusterId === question.questionClusterId
            );
          }
          // For single sub-cluster, get all questions in cluster
          return q.clusterId === question.clusterId && q.type === 'fillBlank';
        })
        .sort((a, b) => {
          // Sort by clusterQuestionIndex if available, otherwise by order field
          if (a.clusterQuestionIndex !== undefined && b.clusterQuestionIndex !== undefined) {
            return a.clusterQuestionIndex - b.clusterQuestionIndex;
          }
          // Fallback to order field (1-based question number in exam)
          const aOrder = (a as any).order || 0;
          const bOrder = (b as any).order || 0;
          return aOrder - bOrder;
        });

      if (clusterQuestions.length > 0) {
        // Replace [blank] with {{blankXX}} sequentially based on question order (1, 2, 3, 4, 5)
        // Map each [blank] to the corresponding question's blankId
        let blankIndex = 0;
        normalized = normalized.replace(/\[blank\]/g, () => {
          // Use clusterQuestionIndex + 1 to get 1-based question number (1, 2, 3, 4, 5)
          // Or use blankIndex + 1 if clusterQuestionIndex is not available
          const question = clusterQuestions[blankIndex];
          let blankNumber = blankIndex + 1; // Default to 1-based index

          if (question) {
            // Try to get blankId from question
            const blankId = getQuestionBlankId(question);
            if (blankId) {
              const match = blankId.match(/blank(\d+)/i);
              if (match) {
                blankNumber = parseInt(match[1], 10);
              } else if (question.clusterQuestionIndex !== undefined) {
                // Use clusterQuestionIndex + 1 for 1-based question number
                blankNumber = question.clusterQuestionIndex + 1;
              }
            } else if (question.clusterQuestionIndex !== undefined) {
              // Use clusterQuestionIndex + 1 for 1-based question number
              blankNumber = question.clusterQuestionIndex + 1;
            }
          }

          blankIndex++;
          return `{{blank${blankNumber}}}`;
        });
      }
    }

    return normalized;
  };

  // Get passage from stimulusData.stimulusText (contains [blank]) or fallback to clusterContext.content
  // CRITICAL: For hasMultipleSubClusters, use getStimulusData() to get the correct passage for questionClusterId
  const clozePassage = useMemo(() => {
    // CRITICAL: Use getStimulusData() to handle both single and multiple sub-clusters
    // For hasMultipleSubClusters: true, this will get passage from stimulusDataMap[questionClusterId]
    // For single sub-cluster: this will get passage from stimulusData.stimulusText
    const stimulusData = getStimulusData();

    if (stimulusData?.stimulusText) {
      let passage = stimulusData.stimulusText;
      // CRITICAL: Decode HTML entities to ensure [blank] markers are found
      passage = passage
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
      return passage;
    }

    // Also check stimulusData.content (some formats use content instead of stimulusText)
    if (stimulusData?.content) {
      let passage = stimulusData.content;
      // CRITICAL: Decode HTML entities to ensure [blank] markers are found
      passage = passage
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
      return passage;
    }

    // Fallback: try to get passage from content.passage of first question in cluster
    // This handles STATIC exams where each question has content.passage but no shared stimulusData
    // CRITICAL: For multiple sub-clusters, filter by questionClusterId to get the correct question
    if (allQuestions && question.clusterContext) {
      const clusterQuestions = allQuestions
        .filter(q => {
          // For multiple sub-clusters, only get questions with the same questionClusterId
          if (question.questionClusterId) {
            return (
              q.clusterId === question.clusterId &&
              q.type === 'fillBlank' &&
              (q as any).questionClusterId === question.questionClusterId
            );
          }
          // For single sub-cluster, get all questions in cluster
          return q.clusterId === question.clusterId && q.type === 'fillBlank';
        })
        .sort((a, b) => {
          if (a.clusterQuestionIndex !== undefined && b.clusterQuestionIndex !== undefined) {
            return a.clusterQuestionIndex - b.clusterQuestionIndex;
          }
          const aOrder = (a as any).order || 0;
          const bOrder = (b as any).order || 0;
          return aOrder - bOrder;
        });

      if (clusterQuestions.length > 0) {
        const firstQuestion = clusterQuestions[0];
        // Check if question has content.passage (for STATIC exams)
        if ((firstQuestion as any).content?.passage) {
          let passage = (firstQuestion as any).content.passage;
          // Decode HTML entities if needed
          passage = passage
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'");
          return passage;
        }
      }
    }

    // Last resort: fallback to content if stimulusText is not available
    return question.clusterContext?.content || '';
  }, [
    question.clusterContext,
    allQuestions,
    question.clusterId,
    question.questionClusterId,
    getStimulusData,
  ]);

  const normalizedClozeTemplate = useMemo(
    () => normalizeClozeTemplate(clozePassage),
    [clozePassage]
  );

  // Helper function to check if this is a multi-blank cloze question
  // (fillBlank type in a cluster with multiple questions sharing a passage)
  // CRITICAL: For multiple sub-clusters, check questions with the same questionClusterId
  const isMultiBlankCloze = useCallback(() => {
    // Check passage (from stimulusData.stimulusText or content) for [blank] placeholders
    const hasNormalizedPlaceholders = normalizedClozeTemplate.includes('{{blank');
    const hasLegacyPlaceholders = clozePassage.includes('______');
    const hasBracketPlaceholders = clozePassage.includes('[blank]'); // Support [blank] format

    // For multiple sub-clusters, count questions with the same questionClusterId
    // For single sub-cluster, count all questions in cluster
    let questionCount = question.clusterQuestionCount || 0;
    if (question.questionClusterId && allQuestions) {
      const sameSubClusterQuestions = allQuestions.filter(
        q =>
          q.clusterId === question.clusterId &&
          q.type === 'fillBlank' &&
          (q as any).questionClusterId === question.questionClusterId
      );
      if (sameSubClusterQuestions.length > 0) {
        questionCount = sameSubClusterQuestions.length;
      }
    }

    return (
      question.type === 'fillBlank' &&
      question.clusterContext &&
      questionCount > 1 &&
      (hasNormalizedPlaceholders || hasLegacyPlaceholders || hasBracketPlaceholders)
    );
  }, [question, normalizedClozeTemplate, clozePassage, allQuestions]);

  // Helper function to get all answers from cluster questions
  // CRITICAL: Returns answers keyed by questionId, NOT blankId
  const getAllClusterAnswers = () => {
    if (!allAnswers || !isMultiBlankCloze() || !allQuestions) {
      return {};
    }

    // CRITICAL: For multiple sub-clusters, only get questions with the same questionClusterId
    // For single sub-cluster, get all questions in cluster
    const clusterQuestions = allQuestions.filter(q => {
      // For multiple sub-clusters, only get questions with the same questionClusterId
      if (question.questionClusterId) {
        return (
          q.clusterId === question.clusterId &&
          q.type === 'fillBlank' &&
          (q as any).questionClusterId === question.questionClusterId
        );
      }
      // For single sub-cluster, get all questions in cluster
      return q.clusterId === question.clusterId && q.type === 'fillBlank';
    });

    const clusterAnswers: Record<string, string> = {};

    // Extract answers from all cluster questions
    // CRITICAL: Use questionId as key, NOT blankId
    clusterQuestions.forEach(clusterQuestion => {
      const questionAnswer = allAnswers[clusterQuestion.id];
      if (questionAnswer?.value) {
        if (typeof questionAnswer.value === 'string') {
          // Direct string answer - use questionId as key
          clusterAnswers[clusterQuestion.id] = questionAnswer.value;
        } else if (typeof questionAnswer.value === 'object' && questionAnswer.value !== null) {
          // Object format - could be keyed by questionId or blankId (legacy)
          Object.entries(questionAnswer.value as Record<string, string>).forEach(([key, val]) => {
            // If key is a questionId (long ID), use it directly
            // If key is a blankId (blank0, blank1, etc.), we need to find the questionId
            if (key.length > 10 && !key.match(/^blank\d+$/i)) {
              // Key looks like a questionId - use it directly
              clusterAnswers[key] = String(val || '');
            } else {
              // Key looks like a blankId - find the questionId for this blank
              // For cluster questions, we need to map blankId → questionId by position
              const placeholderIds = extractTemplatePlaceholderIds();
              const position = placeholderIds.findIndex(id => {
                const normalized = normalizeBlankId(id);
                return normalized === normalizeBlankId(key);
              });

              if (position >= 0 && clusterQuestions[position]) {
                const targetQuestionId = clusterQuestions[position].id;
                clusterAnswers[targetQuestionId] = String(val || '');
              }
            }
          });
        }
      }
    });

    return clusterAnswers;
  };

  // Helper function to create mapping from blank IDs to logical question numbers
  // within the cloze cluster (1, 2, 3, 4, 5, ...) instead of the absolute exam
  // question numbers (21, 22, 23, 24, 25, ...). This keeps the on-screen order
  // and navigation consistent with the cluster instructions and blank labels.
  const getBlankToQuestionNumberMap = useCallback(() => {
    if (!isMultiBlankCloze() || !allQuestions) {
      return {};
    }

    const mapping: Record<string, number> = {};

    // CRITICAL: For multiple sub-clusters, only get questions with the same questionClusterId
    // For single sub-cluster, get all questions in cluster
    const clusterQuestions = allQuestions
      .filter(q => {
        // For multiple sub-clusters, only get questions with the same questionClusterId
        if (question.questionClusterId) {
          return (
            q.clusterId === question.clusterId &&
            q.type === 'fillBlank' &&
            (q as any).questionClusterId === question.questionClusterId
          );
        }
        // For single sub-cluster, get all questions in cluster
        return q.clusterId === question.clusterId && q.type === 'fillBlank';
      })
      .sort((a, b) => {
        // Sort by clusterQuestionIndex if available, otherwise by order field
        if (a.clusterQuestionIndex !== undefined && b.clusterQuestionIndex !== undefined) {
          return a.clusterQuestionIndex - b.clusterQuestionIndex;
        }
        // Fallback to order field (1-based question number in exam)
        const aOrder = (a as any).order || 0;
        const bOrder = (b as any).order || 0;
        return aOrder - bOrder;
      });

    // Get placeholder IDs in order of appearance (these are the blankIds used in items)
    // CRITICAL: We need to map the actual blankIds from placeholderIds, not just backend blankIds
    const placeholderIds = extractTemplatePlaceholderIds();

    // Create mapping: blankId -> logical question number within the cluster (1-based)
    // CRITICAL: Map based on position in placeholderIds array, which matches the order in passage
    // Position 0 in placeholderIds = clusterQuestionIndex 0 = question number 1
    placeholderIds.forEach((blankId, position) => {
      const clusterQuestion = clusterQuestions[position];
      if (!clusterQuestion) {
        return;
      }

      // Use clusterQuestionIndex + 1 as the logical number (1, 2, 3, 4, 5)
      // CRITICAL: If clusterQuestionIndex is undefined, use position (which matches the order in placeholderIds)
      // Position 0 → question 1, Position 1 → question 2, etc.
      let logicalNumber: number;
      if (
        clusterQuestion.clusterQuestionIndex !== undefined &&
        clusterQuestion.clusterQuestionIndex !== null
      ) {
        logicalNumber = clusterQuestion.clusterQuestionIndex + 1; // 1-based (1, 2, 3, 4, 5)
      } else {
        // Fallback: use position in placeholderIds array (1-based)
        // This ensures position 0 → 1, position 1 → 2, position 2 → 3, etc.
        logicalNumber = position + 1;
      }

      // CRITICAL: Map the actual blankId from placeholderIds to logical number
      // This is the blankId that will be used in items, so it MUST be mapped
      // PRIORITY: Position-based mapping takes precedence over extracted number
      // IMPORTANT: Always set this FIRST to ensure position-based mapping is never overridden
      const normalizedBlankId = normalizeBlankId(blankId) || blankId;
      // CRITICAL: Never override if already set (position-based mapping is authoritative)
      if (mapping[blankId] === undefined) {
        mapping[blankId] = logicalNumber;
      }
      if (mapping[normalizedBlankId] === undefined) {
        mapping[normalizedBlankId] = logicalNumber;
      }

      // Map the standard blankId based on logical number (blank1, blank2, blank3, blank4, blank5)
      const standardBlankId = `blank${logicalNumber}`;
      mapping[standardBlankId] = logicalNumber;

      // Also map 0-based blankIds (blank0, blank1, blank2, blank3, blank4) to logical number
      const zeroBasedBlankId = `blank${position}`;
      mapping[zeroBasedBlankId] = logicalNumber;

      // Also map backend blankId if different (e.g., blank0 -> 1, blank1 -> 2)
      // CRITICAL: Only map if it doesn't conflict with existing position-based mapping
      const backendBlankId = getQuestionBlankId(clusterQuestion);
      if (backendBlankId && backendBlankId !== blankId) {
        const normalizedBackendBlankId = normalizeBlankId(backendBlankId) || backendBlankId;
        // Only map if it doesn't already exist (to prevent override of position-based mapping)
        if (!mapping[backendBlankId]) {
          mapping[backendBlankId] = logicalNumber;
        }
        if (!mapping[normalizedBackendBlankId]) {
          mapping[normalizedBackendBlankId] = logicalNumber;
        }
      }

      // CRITICAL: Do NOT map extracted number from blankId if it conflicts with position
      // Example: blank3 at position 2 should map to logicalNumber 3 (from position), NOT 4 (from extracted number)
      // Only map extracted number if it matches the position (for backward compatibility)
      // IMPORTANT: This must be done AFTER setting blankId mapping to ensure position-based mapping takes precedence
      const match = blankId.match(/blank(\d+)/i);
      if (match) {
        const extractedNumber = parseInt(match[1], 10);
        // Only map extracted number if:
        // 1. It matches position (0-based) - e.g., blank0 at position 0
        // 2. It matches logicalNumber (1-based) - e.g., blank1 at position 0 (logicalNumber 1)
        // 3. The mapping doesn't already exist (to prevent override)
        // This prevents blank3 at position 2 from mapping to 4
        const extractedBlankId = `blank${extractedNumber}`;
        if (
          (extractedNumber === position || extractedNumber === logicalNumber) &&
          !mapping[extractedBlankId]
        ) {
          mapping[extractedBlankId] = logicalNumber;
        }
      }
    });

    return mapping;
  }, [
    isMultiBlankCloze,
    allQuestions,
    question.clusterId,
    question.questionClusterId,
    normalizeBlankId,
    getQuestionBlankId,
    normalizedClozeTemplate,
    question.clusterContext?.content,
  ]);

  // Helper function to create mapping from blank IDs to question indices (0-based)
  const getBlankToQuestionIndexMap = useCallback(() => {
    if (!isMultiBlankCloze() || !allQuestions) {
      return {};
    }

    const mapping: Record<string, number> = {};

    // CRITICAL: For multiple sub-clusters, only get questions with the same questionClusterId
    // For single sub-cluster, get all questions in cluster
    const clusterQuestions = allQuestions
      .filter(q => {
        // For multiple sub-clusters, only get questions with the same questionClusterId
        if (question.questionClusterId) {
          return (
            q.clusterId === question.clusterId &&
            q.type === 'fillBlank' &&
            (q as any).questionClusterId === question.questionClusterId
          );
        }
        // For single sub-cluster, get all questions in cluster
        return q.clusterId === question.clusterId && q.type === 'fillBlank';
      })
      .sort((a, b) => {
        // Sort by clusterQuestionIndex if available, otherwise by order field
        if (a.clusterQuestionIndex !== undefined && b.clusterQuestionIndex !== undefined) {
          return a.clusterQuestionIndex - b.clusterQuestionIndex;
        }
        // Fallback to order field (1-based question number in exam)
        const aOrder = (a as any).order || 0;
        const bOrder = (b as any).order || 0;
        return aOrder - bOrder;
      });

    // Create mapping: blankId -> question index (0-based)
    clusterQuestions.forEach((clusterQuestion, index) => {
      const blankId = getQuestionBlankId(clusterQuestion);

      // Find the actual question index in allQuestions
      const actualQuestionIndex = allQuestions.findIndex(q => q.id === clusterQuestion.id);
      if (actualQuestionIndex < 0) return;

      // If blankId exists, use it (and normalized version)
      if (blankId) {
        const normalizedBlankId = normalizeBlankId(blankId);
        if (normalizedBlankId) {
          mapping[blankId] = actualQuestionIndex;
          mapping[normalizedBlankId] = actualQuestionIndex;
        }
      }

      // Also create mapping from clusterQuestionIndex + 1 (1-based) to ensure we can find it
      // This matches how createFillBlankItems() creates item.id
      if (clusterQuestion.clusterQuestionIndex !== undefined) {
        const fallbackBlankId = `blank${clusterQuestion.clusterQuestionIndex + 1}`;
        mapping[fallbackBlankId] = actualQuestionIndex;
      } else {
        // Fallback to index + 1 (1-based)
        const fallbackBlankId = `blank${index + 1}`;
        mapping[fallbackBlankId] = actualQuestionIndex;
      }
    });

    return mapping;
  }, [isMultiBlankCloze, allQuestions, question.clusterId]);

  // Helper function to extract placeholder IDs from template in order of appearance
  // CRITICAL: Return placeholders in the order they appear in the passage
  // This order will map to clusterQuestionIndex (position 0 → clusterQuestionIndex 0, etc.)
  const extractTemplatePlaceholderIds = useCallback(() => {
    const placeholderIds: string[] = [];
    let templateSource = normalizedClozeTemplate || question.clusterContext?.content || '';

    // CRITICAL: Decode HTML entities before extracting placeholders
    // This ensures [blank] markers are found even if passage has &lt;span&gt; tags
    templateSource = templateSource
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    // Extract {{blankXX}} format - preserve order
    templateSource.replace(/\{\{([^}]+)\}\}/g, (_, raw) => {
      const normalized = normalizeBlankId(raw);
      if (normalized?.startsWith('blank')) {
        placeholderIds.push(normalized);
      }
      return '';
    });

    // Extract (XX) ______ format - preserve order
    templateSource.replace(/\((\d+)\)\s*______/g, (_, digits) => {
      const blankId = `blank${digits}`;
      placeholderIds.push(blankId);
      return '';
    });

    // Extract [blank] format - preserve order
    // Count [blank] occurrences and generate blankIds based on position
    // CRITICAL: For multiple sub-clusters, only use questions with the same questionClusterId
    if (templateSource.includes('[blank]') && allQuestions) {
      const clusterQuestions = allQuestions
        ? allQuestions
            .filter(q => {
              // For multiple sub-clusters, only get questions with the same questionClusterId
              if (question.questionClusterId) {
                return (
                  q.clusterId === question.clusterId &&
                  q.type === 'fillBlank' &&
                  (q as any).questionClusterId === question.questionClusterId
                );
              }
              // For single sub-cluster, get all questions in cluster
              return q.clusterId === question.clusterId && q.type === 'fillBlank';
            })
            .sort((a, b) => {
              // Sort by clusterQuestionIndex if available, otherwise by order field
              if (a.clusterQuestionIndex !== undefined && b.clusterQuestionIndex !== undefined) {
                return a.clusterQuestionIndex - b.clusterQuestionIndex;
              }
              // Fallback to order field (1-based question number in exam)
              const aOrder = (a as any).order || 0;
              const bOrder = (b as any).order || 0;
              return aOrder - bOrder;
            })
        : [];

      // Count [blank] occurrences
      const blankMatches = templateSource.match(/\[blank\]/gi);
      if (blankMatches) {
        blankMatches.forEach((_, index) => {
          // Generate blankId based on position (which corresponds to clusterQuestionIndex)
          // Position 0 → clusterQuestionIndex 0 → blank0 or blank1 (1-based)
          const blankNumber = index + 1; // 1-based (1, 2, 3, 4, 5)
          placeholderIds.push(`blank${blankNumber}`);
        });
      }
    }

    // CRITICAL: Ensure we have the same number of placeholders as questions in cluster
    // If we have fewer placeholders than questions, add missing ones
    // CRITICAL: For multiple sub-clusters, only count questions with the same questionClusterId
    if (allQuestions) {
      const clusterQuestions = allQuestions
        .filter(q => {
          // For multiple sub-clusters, only get questions with the same questionClusterId
          if (question.questionClusterId) {
            return (
              q.clusterId === question.clusterId &&
              q.type === 'fillBlank' &&
              (q as any).questionClusterId === question.questionClusterId
            );
          }
          // For single sub-cluster, get all questions in cluster
          return q.clusterId === question.clusterId && q.type === 'fillBlank';
        })
        .sort((a, b) => {
          if (a.clusterQuestionIndex !== undefined && b.clusterQuestionIndex !== undefined) {
            return a.clusterQuestionIndex - b.clusterQuestionIndex;
          }
          const aOrder = (a as any).order || 0;
          const bOrder = (b as any).order || 0;
          return aOrder - bOrder;
        });

      // If we have fewer placeholders than questions, add missing ones based on clusterQuestionIndex
      while (placeholderIds.length < clusterQuestions.length) {
        const position = placeholderIds.length;
        const blankNumber = position + 1; // 1-based (1, 2, 3, 4, 5)
        placeholderIds.push(`blank${blankNumber}`);
      }
    }

    return placeholderIds;
  }, [
    normalizedClozeTemplate,
    question.clusterContext?.content,
    allQuestions,
    question.clusterId,
    question.questionClusterId,
    normalizeBlankId,
  ]);

  // Create mapping from position in passage (0-based) to questionId
  // CRITICAL: Map by clusterQuestionIndex, NOT by blankId
  // Position 0 in passage = question with clusterQuestionIndex 0
  // Position 1 in passage = question with clusterQuestionIndex 1
  // etc.
  const getPositionToQuestionIdMap = useCallback(() => {
    if (!isMultiBlankCloze() || !allQuestions) {
      return {};
    }

    const mapping: Record<number, string> = {};

    // CRITICAL: For multiple sub-clusters, only get questions with the same questionClusterId
    // For single sub-cluster, get all questions in cluster
    const clusterQuestions = allQuestions
      ? allQuestions
          .filter(q => {
            // For multiple sub-clusters, only get questions with the same questionClusterId
            if (question.questionClusterId) {
              return (
                q.clusterId === question.clusterId &&
                q.type === 'fillBlank' &&
                (q as any).questionClusterId === question.questionClusterId
              );
            }
            // For single sub-cluster, get all questions in cluster
            return q.clusterId === question.clusterId && q.type === 'fillBlank';
          })
          .sort((a, b) => {
            // Sort by clusterQuestionIndex if available, otherwise by order field
            if (a.clusterQuestionIndex !== undefined && b.clusterQuestionIndex !== undefined) {
              return a.clusterQuestionIndex - b.clusterQuestionIndex;
            }
            // Fallback to order field (1-based question number in exam)
            const aOrder = (a as any).order || 0;
            const bOrder = (b as any).order || 0;
            return aOrder - bOrder;
          })
      : [];

    // Map position (0-based index in passage) to questionId by clusterQuestionIndex
    // Position 0 → clusterQuestionIndex 0
    // Position 1 → clusterQuestionIndex 1
    // etc.
    clusterQuestions.forEach((clusterQuestion, index) => {
      mapping[index] = clusterQuestion.id;
    });

    return mapping;
  }, [isMultiBlankCloze, allQuestions, question.clusterId]);

  const createFillBlankItems = useCallback(() => {
    if (!isMultiBlankCloze() || !question.clusterContext) {
      return [];
    }

    const buildDefaultItem = (blankId: string, position: number) => ({
      id: blankId,
      key: blankId,
      position, // Store position for mapping to questionId
      correctAnswers: [],
      caseSensitive: false,
      trimWhitespace: true,
      inputType: 'text' as const,
      placeholder: t('landing.questions.placeholder.enterAnswer'),
      points:
        question.points && question.clusterQuestionCount
          ? Math.max(1, Math.round(question.points / question.clusterQuestionCount))
          : question.points || 0,
      subject: question.subject,
    });

    // CRITICAL: For multiple sub-clusters, only get questions with the same questionClusterId
    // For single sub-cluster, get all questions in cluster
    const clusterQuestions = allQuestions
      ? allQuestions
          .filter(q => {
            // For multiple sub-clusters, only get questions with the same questionClusterId
            if (question.questionClusterId) {
              return (
                q.clusterId === question.clusterId &&
                q.type === 'fillBlank' &&
                (q as any).questionClusterId === question.questionClusterId
              );
            }
            // For single sub-cluster, get all questions in cluster
            return q.clusterId === question.clusterId && q.type === 'fillBlank';
          })
          .sort((a, b) => {
            // Sort by clusterQuestionIndex if available, otherwise by order field
            if (a.clusterQuestionIndex !== undefined && b.clusterQuestionIndex !== undefined) {
              return a.clusterQuestionIndex - b.clusterQuestionIndex;
            }
            // Fallback to order field (1-based question number in exam)
            const aOrder = (a as any).order || 0;
            const bOrder = (b as any).order || 0;
            return aOrder - bOrder;
          })
      : [];

    // Extract placeholder IDs from template in order of appearance
    const placeholderIds = extractTemplatePlaceholderIds();

    // Create items based on placeholder order (position in passage)
    // CRITICAL: Map by clusterQuestionIndex, NOT by blankId
    // Position 0 in passage = question with clusterQuestionIndex 0
    // Position 1 in passage = question with clusterQuestionIndex 1
    // etc.
    const items = placeholderIds.map((blankId, position) => {
      // Get question by position (which corresponds to clusterQuestionIndex)
      // clusterQuestions is already sorted by clusterQuestionIndex
      const clusterQuestion = clusterQuestions[position];

      if (clusterQuestion) {
        // CRITICAL: Calculate questionNumber from questionIndex in allQuestions (same as header)
        // This ensures placeholder matches the actual question number shown in header (e.g., 21, 22, 23, 24, 25)
        // Priority 1: Find questionIndex in allQuestions array (most reliable)
        // Priority 2: Calculate from current questionIndex + clusterQuestionIndex offset
        // Priority 3: Fallback to clusterQuestionIndex + 1 (relative to cluster)
        let questionNumber: number;

        if (allQuestions) {
          // Find the index of clusterQuestion in allQuestions array
          const clusterQuestionIndexInAll = allQuestions.findIndex(
            q => q.id === clusterQuestion.id
          );
          if (clusterQuestionIndexInAll >= 0) {
            // Use questionIndex + 1 (same calculation as header)
            questionNumber = clusterQuestionIndexInAll + 1;
          } else if (
            clusterQuestion.clusterQuestionIndex !== undefined &&
            question.clusterQuestionIndex !== undefined
          ) {
            // Calculate from current questionIndex + offset
            const currentClusterIndex = question.clusterQuestionIndex;
            const targetClusterIndex = clusterQuestion.clusterQuestionIndex;
            const offset = targetClusterIndex - currentClusterIndex;
            questionNumber = questionIndex + 1 + offset;
          } else if (
            clusterQuestion.clusterQuestionIndex !== undefined &&
            clusterQuestion.clusterQuestionIndex !== null
          ) {
            // Fallback: use clusterQuestionIndex + 1 (relative to cluster: 1, 2, 3, 4, 5)
            questionNumber = clusterQuestion.clusterQuestionIndex + 1;
          } else {
            // Last resort: use position + 1
            questionNumber = position + 1;
          }
        } else if (
          clusterQuestion.clusterQuestionIndex !== undefined &&
          question.clusterQuestionIndex !== undefined
        ) {
          // Calculate from current questionIndex + offset (when allQuestions is not available)
          const currentClusterIndex = question.clusterQuestionIndex;
          const targetClusterIndex = clusterQuestion.clusterQuestionIndex;
          const offset = targetClusterIndex - currentClusterIndex;
          questionNumber = questionIndex + 1 + offset;
        } else if (
          clusterQuestion.clusterQuestionIndex !== undefined &&
          clusterQuestion.clusterQuestionIndex !== null
        ) {
          // Fallback: use clusterQuestionIndex + 1 (relative to cluster: 1, 2, 3, 4, 5)
          questionNumber = clusterQuestion.clusterQuestionIndex + 1;
        } else {
          // Last resort: use position + 1
          questionNumber = position + 1;
        }

        const item = {
          id: blankId, // Use blankId from template for display/rendering
          key: blankId,
          position, // Store position for mapping to questionId
          questionId: clusterQuestion.id, // Store actual questionId for submission (based on clusterQuestionIndex)
          clusterQuestionIndex: clusterQuestion.clusterQuestionIndex, // Store clusterQuestionIndex for direct question number calculation
          questionNumber, // Store calculated question number (1-based)
          correctAnswers: [],
          caseSensitive: false,
          trimWhitespace: true,
          inputType: 'text' as const,
          placeholder: `${questionNumber}`, // Use questionNumber directly, not from blankId lookup
          points: clusterQuestion.points || 6,
          subject: clusterQuestion.subject || question.subject,
        };
        return item;
      }

      // Fallback: create default item if no matching question found
      return buildDefaultItem(blankId, position);
    });

    return items;
  }, [
    question,
    questionIndex,
    allQuestions,
    question.questionClusterId,
    isMultiBlankCloze,
    t,
    extractTemplatePlaceholderIds,
  ]);

  // Helper function to render question answers based on type
  const renderQuestionAnswers = () => {
    // Check if we're in a cluster context that already has a question stem card
    const hasClusterQuestionStem =
      question.clusterContext &&
      (getQuestionLayout() === 'side-by-side' || getQuestionLayout() === 'row-by-row');

    switch (question.type) {
      case 'mc':
        return (
          <div className='space-y-4'>
            {/* Question Stem - only if not in cluster with existing stem and has content */}
            {/* AND only if not side-by-side layout (stem already shown) */}
            {!hasClusterQuestionStem &&
              getQuestionLayout() !== 'side-by-side' &&
              question.stem?.trim() &&
              question.stem.trim().length > 0 && (
                <Card className='p-4 bg-card/80 backdrop-blur-sm rounded-xl border border-border shadow-sm hover:shadow-md transition-all duration-300 mb-4'>
                  <HtmlContent
                    content={question.stem}
                    className='text-base font-semibold text-foreground'
                  />
                </Card>
              )}

            {/* Multiple Choice Options */}
            {Array.isArray(question.options) && question.options.length > 0 ? null : (
              <Card className='p-4 border border-amber-200 bg-amber-50 text-amber-700 text-sm'>
                {t('landing.questions.types.unsupportedOptions', {
                  defaultValue: 'Câu hỏi chưa được cấu hình đáp án hợp lệ.',
                })}
              </Card>
            )}
            <RadioGroup
              value={answer?.value?.toString() || ''}
              onValueChange={val => {
                const usesObjectOptions =
                  question.options?.some(option => typeof option === 'object') || false;
                if (usesObjectOptions) {
                  onChange(val);
                } else {
                  onChange(parseInt(val, 10));
                }
              }}
              disabled={isExpired}
              className='space-y-3'
            >
              {question.options?.map((option, index) => {
                const optionLetter = String.fromCharCode(65 + index); // A, B, C, D
                const optionValue =
                  typeof option === 'object' && option !== null && 'id' in option
                    ? ((option as { id?: string }).id ?? index.toString())
                    : index.toString();
                const isSelected = answer?.value?.toString() === optionValue.toString();

                // Handle both old format (string[]) and new format ({id, content}[])
                const optionContent =
                  typeof option === 'string' ? option : (option as any)?.content || '';
                const usesObjectOptions =
                  typeof option === 'object' && option !== null && 'id' in option;

                const handleSelect = () => {
                  if (isExpired) return;
                  if (usesObjectOptions) {
                    onChange(optionValue.toString());
                  } else {
                    onChange(index);
                  }
                };

                return (
                  <Card
                    key={index}
                    className={cn(
                      'group hover:shadow-lg transition-all duration-300 hover:ring-2 hover:ring-primary/20 bg-card/80 backdrop-blur-sm rounded-xl shadow-lg border border-border cursor-pointer',
                      isSelected &&
                        'ring-2 ring-primary/500 dark:ring-primary/400 bg-primary/10/50 dark:bg-primary/20/20',
                      'transition-all duration-300'
                    )}
                    onClick={handleSelect}
                  >
                    <div className='p-4'>
                      <div className='flex items-center space-x-4'>
                        <div
                          className={cn(
                            'w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm transition-all duration-300',
                            isSelected
                              ? 'bg-gradient-to-br from-primary to-primary/80 text-white shadow-lg shadow-primary/25'
                              : 'bg-gradient-to-br from-muted to-muted/80 text-foreground group-hover:from-primary/20 group-hover:to-primary/30 dark:group-hover:from-primary/20 dark:group-hover:to-primary/30'
                          )}
                        >
                          {optionLetter}
                        </div>
                        <div className='flex-1'>
                          <Label
                            htmlFor={`option-${index}`}
                            className='text-lg font-semibold text-gray-800 dark:text-white leading-relaxed cursor-pointer'
                          >
                            <HtmlContent content={optionContent} />
                          </Label>
                        </div>
                        <RadioGroupItem
                          value={optionValue.toString()}
                          id={`option-${index}`}
                          className='sr-only'
                        />
                      </div>
                    </div>
                  </Card>
                );
              })}
            </RadioGroup>
          </div>
        );

      case 'trueFalse': {
        // Resolve passage/stimulus text for True/False questions:
        // - If trueFalseContext is explicitly set, use it.
        // - If there's a clusterContext: stimulus is already shown in the left column cluster header
        //   → do NOT pass it again to TrueFalseQuestion to avoid duplication.
        // - If NO clusterContext (standalone question): fall back to getStimulusData() so the
        //   TrueFalseQuestion can render the passage in its own left panel.
        const resolvedTFContext = (() => {
          if (question.trueFalseContext) {
            return {
              ...question.trueFalseContext,
              content:
                typeof question.trueFalseContext.content === 'string'
                  ? question.trueFalseContext.content
                  : JSON.stringify(question.trueFalseContext.content),
            };
          }
          // Only use getStimulusData() as fallback when there is NO clusterContext.
          // When clusterContext exists, the stimulus is displayed in the cluster header (left column)
          // and passing it here would create a duplicate.
          if (!question.clusterContext) {
            const stimulusData = getStimulusData() as any;
            if (stimulusData) {
              const passageText = stimulusData.stimulusText || stimulusData.content || '';
              if (passageText) {
                return {
                  type: 'text' as const,
                  title: '',
                  content: passageText,
                };
              }
            }
          }
          return undefined;
        })();

        return (
          <div className='space-y-4'>
            {/* True/False Items */}
            <TrueFalseQuestion
              items={(question.trueFalseItems || []).map(item => ({
                ...item,
                statement:
                  typeof item.statement === 'string'
                    ? item.statement
                    : JSON.stringify(item.statement),
              }))}
              context={resolvedTFContext}
              title={(question as any).title || undefined}
              value={(answer?.value as Record<string, boolean>) || {}}
              onChange={onChange}
              readOnly={isExpired}
              examStandard={examStandard}
              subject={examSubject || question.subject}
            />
          </div>
        );
      }

      case 'matching':
        return (
          <div className='space-y-4'>
            {/* Matching Question */}
            <MatchingQuestion
              prompts={(question.matchingPrompts || []).map((prompt, idx) => ({
                ...prompt,
                label:
                  typeof prompt.label === 'string' ? prompt.label : JSON.stringify(prompt.label),
                order: idx,
              }))}
              options={(question.matchingOptions || []).map((option, idx) => ({
                ...option,
                label:
                  typeof option.label === 'string' ? option.label : JSON.stringify(option.label),
                order: idx,
              }))}
              correct={question.matchingCorrect || {}}
              questionNumber={question.stem || `Question ${questionIndex + 1}`}
              value={(answer?.value as Record<string, string | null>) || {}}
              onChange={onChange}
              readOnly={isExpired}
              shuffledOrder={shuffledOrders?.[question.id]}
              onShuffledOrderChange={order => onShuffledOrderChange?.(question.id, order)}
            />
          </div>
        );

      case 'short':
        // short (from short_numeric) - treat same as short_text and fillBlank
        // Use FillBlankQuestion for consistent behavior with fillAnswers submission
        return (
          <div className='space-y-4'>
            {/* Short Answer - use FillBlankQuestion for consistency with submission format */}
            <FillBlankQuestion
              value={(answer?.value as string) || ''}
              onChange={onChange}
              readOnly={isExpired}
              placeholder={t('landing.questions.placeholder.enterAnswer')}
            />
          </div>
        );

      case 'essay':
        return (
          <div className='space-y-4'>
            {/* Question Stem - only if not in cluster with existing stem and has content */}
            {!hasClusterQuestionStem &&
              question.stem?.trim() &&
              question.stem.trim().length > 0 && (
                <Card className='p-4 bg-card/80 backdrop-blur-sm rounded-xl border border-border shadow-sm hover:shadow-md transition-all duration-300 mb-4'>
                  <HtmlContent
                    content={question.stem}
                    className='text-base font-semibold text-foreground'
                  />
                </Card>
              )}

            {/* Essay Question */}
            <EssayQuestion
              value={(answer?.value as string) || ''}
              onChange={onChange}
              minWords={question.minWords ?? 0}
              maxWords={question.maxWords ?? 600}
              readOnly={isExpired}
            />
          </div>
        );

      case 'fillBlank':
      case 'short_text':
        return (
          <div className='space-y-4'>
            {/* Legacy Fill in the Blank */}
            <FillBlankQuestion
              value={(answer?.value as string) || ''}
              onChange={onChange}
              readOnly={isExpired}
              placeholder={t('landing.questions.placeholder.enterAnswer')}
            />
          </div>
        );

      default:
        return (
          <div className='text-center text-muted-foreground'>
            {t('landing.questions.unsupportedType', { type: question.type })}
          </div>
        );
    }
  };

  return (
    <div className='flex flex-col'>
      {/* Question Header */}
      <div className='flex-shrink-0'>
        <QuestionHeader
          questionNumber={questionIndex + 1}
          totalQuestions={totalQuestions}
          subject={examSubject || question.subject || 'Tổng hợp'}
          questionType={getQuestionTypeLabel()}
          points={question.points}
          isFlagged={answer?.flagged || false}
          onToggleFlag={onToggleFlag}
        />
      </div>

      {/* Content Area */}
      <div className='space-y-4'>
        {/* Special handling for multi-blank cloze questions (fillBlank in cluster with multiple questions) */}
        {isMultiBlankCloze() && question.clusterContext ? (
          <div className='space-y-4'>
            {/* Part Header Card - Show for all questions like other parts */}
            {shouldShowPartHeader() && (
              <Card className='p-4 bg-gradient-to-r from-primary/10 to-accent/10 dark:from-primary/20 dark:to-accent/20 border border-primary/30 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 mb-4'>
                <h2 className='text-base font-semibold text-primary/80 dark:text-primary mb-2'>
                  {question.clusterContext.title || getPartTitle()}
                </h2>
                {/* Show instructions (content) from clusterConfig */}
                {getPartDescription() && (
                  <div className='text-sm text-muted-foreground'>
                    <HtmlContent
                      content={getPartDescription()}
                      className='prose prose-sm max-w-none'
                    />
                  </div>
                )}
              </Card>
            )}

            {/* Enhanced Fill in the Blank with Template Support */}
            <EnhancedFillBlankQuestion
              template={normalizedClozeTemplate || clozePassage}
              items={createFillBlankItems()}
              value={getAllClusterAnswers()}
              onChange={questionAnswers => {
                // CRITICAL: questionAnswers is now keyed by questionId (e.g., { "questionId1": "answer1", "questionId2": "answer2" })
                // No more blankId mapping needed - questionId is already the correct key
                // If this is the current question's answer, save it directly
                // questionAnswers should only contain answers for the current question (from EnhancedFillBlankQuestion)
                if (question.id in questionAnswers) {
                  const answerValue = questionAnswers[question.id];
                  onChange(answerValue);
                } else {
                  // Still save it - DemoExam.setAnswer will handle distribution
                  onChange(questionAnswers);
                }
              }}
              readOnly={isExpired}
              questionIndex={questionIndex}
              // CRITICAL: Use actual exam question number (questionIndex + 1) to match item.questionNumber
              // This ensures isCurrentQuestion check works correctly (21 === 21, not 21 === 1)
              currentQuestionNumber={questionIndex + 1}
              allAnswers={getAllClusterAnswers()}
              blankToQuestionNumberMap={useMemo(() => {
                const map = getBlankToQuestionNumberMap();
                return map;
              }, [getBlankToQuestionNumberMap])}
              blankToQuestionIndexMap={getBlankToQuestionIndexMap()}
              onNavigateToQuestion={targetBlankNumber => {
                // Navigate to the target question (targetBlankNumber is the blank number like 1, 2, 3, 4, 5)
                // CRITICAL: Find question by clusterQuestionIndex, NOT by blankId
                // targetBlankNumber - 1 = position in passage = clusterQuestionIndex
                if (onNavigateToQuestion && allQuestions) {
                  // Find the question by clusterQuestionIndex (targetBlankNumber - 1)
                  const targetQuestion = allQuestions.find(q => {
                    if (q.clusterId !== question.clusterId || q.type !== 'fillBlank') return false;
                    return q.clusterQuestionIndex === targetBlankNumber - 1;
                  });

                  if (targetQuestion) {
                    const targetQuestionIndex = allQuestions.findIndex(
                      q => q.id === targetQuestion.id
                    );
                    if (targetQuestionIndex >= 0) {
                      onNavigateToQuestion(targetQuestionIndex);
                    }
                  } else {
                    // Fallback: find by order field if clusterQuestionIndex not available
                    // CRITICAL: For multiple sub-clusters, only search in questions with the same questionClusterId
                    const clusterQuestions = allQuestions
                      .filter(q => {
                        if (q.clusterId !== question.clusterId || q.type !== 'fillBlank')
                          return false;
                        // For multiple sub-clusters, only get questions with the same questionClusterId
                        if (question.questionClusterId) {
                          return (q as any).questionClusterId === question.questionClusterId;
                        }
                        return true;
                      })
                      .sort((a, b) => {
                        const aOrder = (a as any).order || 0;
                        const bOrder = (b as any).order || 0;
                        return aOrder - bOrder;
                      });

                    if (clusterQuestions[targetBlankNumber - 1]) {
                      const targetQuestionIndex = allQuestions.findIndex(
                        q => q.id === clusterQuestions[targetBlankNumber - 1].id
                      );
                      if (targetQuestionIndex >= 0) {
                        onNavigateToQuestion(targetQuestionIndex);
                      }
                    }
                  }
                }
              }}
            />
          </div>
        ) : question.clusterContext ? (
          <div className='space-y-4'>
            {/* Layout-specific rendering */}
            {getQuestionLayout() === 'side-by-side' ? (
              /* Side-by-side layout for Part 1 and Part 2 */
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                {/* Left Column - Part Content First, Then Question Context */}
                <div className='space-y-4'>
                  {/* Part Header Card - Priority 1 (Limited Height for Part 2) */}
                  {shouldShowPartHeader() && question.clusterContext && (
                    <Card className='p-4 bg-gradient-to-r from-primary/10 to-accent/10 dark:from-primary/20 dark:to-accent/20 border border-primary/30 rounded-lg shadow-sm hover:shadow-md transition-all duration-300'>
                      <h2 className='text-base font-semibold text-primary/80 dark:text-primary mb-1'>
                        {question.clusterContext.title || getPartTitle()}
                      </h2>
                      <div className='max-h-64 overflow-y-auto'>
                        <HtmlContent
                          content={getPartDescription()}
                          className='text-sm text-muted-foreground'
                        />
                        {/* Display stimulusData if available (for sub-clusters) */}
                        {getStimulusData() && (
                          <div className='mt-4 pt-4 border-t border-primary/20'>
                            <HtmlContent
                              content={(() => {
                                const stimulusData = getStimulusData();
                                if (typeof stimulusData === 'string') {
                                  return stimulusData;
                                }
                                if (typeof stimulusData === 'object' && stimulusData !== null) {
                                  return (
                                    stimulusData.content ||
                                    stimulusData.stimulusText ||
                                    JSON.stringify(stimulusData)
                                  );
                                }
                                return String(stimulusData);
                              })()}
                              className='text-sm text-muted-foreground'
                            />
                          </div>
                        )}
                      </div>
                    </Card>
                  )}

                  {/* Question Context - Priority 2 */}
                  {(() => {
                    const contentToRender = typeof question.stem === 'string' ? question.stem : '';

                    if (!question.clusterContext || !contentToRender?.trim()) {
                      // No cluster context or no content to render -> skip
                      if (!contentToRender?.trim()) return null;
                      return (
                        <Card className='p-4 bg-card/80 backdrop-blur-sm rounded-xl border border-border shadow-sm hover:shadow-md transition-all duration-300'>
                          {renderClusterMeta()}
                          <HtmlContent
                            content={contentToRender}
                            className='text-base font-semibold text-foreground'
                          />
                        </Card>
                      );
                    }

                    const trimmedContent = contentToRender.trim();
                    let isDuplicate = false;

                    // 1. Check against clusterContext.content (Main Cluster Instructions/Content)
                    if (question.clusterContext.content?.trim() === trimmedContent) {
                      isDuplicate = true;
                    }

                    // 2. Check against resolved Stimulus Data (Shared Context via getStimulusData)
                    if (!isDuplicate) {
                      const stimulusData = getStimulusData();
                      if (stimulusData) {
                        let stimulusContent = '';
                        if (typeof stimulusData === 'string') {
                          stimulusContent = stimulusData;
                        } else if (typeof stimulusData === 'object' && stimulusData !== null) {
                          stimulusContent = stimulusData.content || stimulusData.stimulusText || '';
                        }

                        if (stimulusContent && stimulusContent.trim() === trimmedContent) {
                          isDuplicate = true;
                        }
                      }
                    }

                    // 3. Check against getPartDescription() (the cluster instruction text)
                    if (!isDuplicate) {
                      const partDesc = getPartDescription();
                      if (partDesc && partDesc.trim() === trimmedContent) {
                        isDuplicate = true;
                      }
                    }

                    if (isDuplicate) return null;

                    return (
                      <Card className='p-4 bg-card/80 backdrop-blur-sm rounded-xl border border-border shadow-sm hover:shadow-md transition-all duration-300'>
                        {renderClusterMeta()}
                        <HtmlContent
                          content={contentToRender}
                          className='text-base font-semibold text-foreground'
                        />
                      </Card>
                    );
                  })()}
                </div>

                {/* Right Column - Question Answers */}
                <div className='space-y-3 flex flex-col'>
                  {/* Question Image */}
                  {question.imageUrl && (
                    <div className='my-3'>
                      <QuestionImage
                        src={question.imageUrl}
                        alt={question.stem}
                        className='w-full h-auto rounded-lg shadow-md'
                      />
                    </div>
                  )}

                  {/* Question Answers */}
                  {renderQuestionAnswers()}
                </div>
              </div>
            ) : (
              /* Default layout for other clusters (row-by-row) */
              <div className='space-y-4'>
                {/* Part Header Card - Priority 1 (Limited Height) */}
                {shouldShowPartHeader() && question.clusterContext && (
                  <Card className='p-4 bg-gradient-to-r from-primary/10 to-accent/10 dark:from-primary/20 dark:to-accent/20 border border-primary/30 rounded-lg shadow-sm hover:shadow-md transition-all duration-300'>
                    <h2 className='text-base font-semibold text-primary/80 dark:text-primary mb-1'>
                      {question.clusterContext.title || getPartTitle()}
                    </h2>
                    <div className='max-h-48 overflow-y-auto'>
                      <HtmlContent
                        content={getPartDescription()}
                        className='text-sm text-muted-foreground'
                      />
                      {/* Display stimulusData if available (for sub-clusters) */}
                      {getStimulusData() && (
                        <div className='mt-4 pt-4 border-t border-primary/20'>
                          <HtmlContent
                            content={(() => {
                              const stimulusData = getStimulusData();
                              if (typeof stimulusData === 'string') {
                                return stimulusData;
                              }
                              if (typeof stimulusData === 'object' && stimulusData !== null) {
                                return (
                                  stimulusData.content ||
                                  stimulusData.stimulusText ||
                                  JSON.stringify(stimulusData)
                                );
                              }
                              return String(stimulusData);
                            })()}
                            className='text-sm text-muted-foreground'
                          />
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* Question Stem (the actual question text) */}
                {question.stem?.trim() && (
                  <Card className='p-4 bg-card/80 backdrop-blur-sm rounded-xl border border-border shadow-sm hover:shadow-md transition-all duration-300'>
                    {renderClusterMeta()}
                    <HtmlContent
                      content={question.stem}
                      className='text-base font-semibold text-foreground'
                    />
                  </Card>
                )}

                <div className='space-y-3'>
                  {/* Question Image */}
                  {question.imageUrl && (
                    <div className='my-3'>
                      <QuestionImage
                        src={question.imageUrl}
                        alt={question.stem}
                        className='w-full h-auto rounded-lg shadow-md'
                      />
                    </div>
                  )}

                  {/* Question Answers */}
                  {renderQuestionAnswers()}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Single column layout for non-cluster questions */
          <>
            {/* Question Image */}
            {question.imageUrl && (
              <div className='my-4'>
                <QuestionImage
                  src={question.imageUrl}
                  alt={question.stem}
                  className='w-full h-auto rounded-lg shadow-md'
                />
              </div>
            )}

            {/* Question Content */}
            {renderQuestionAnswers()}
          </>
        )}
      </div>
    </div>
  );
}
